import {
  Icon,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { useEffect, useRef } from 'react';

import ProfileImage from '@/components/shared/ProfileImage';
import type { AgentInfo } from '../utils/AgentInfo';
import SelectionCardsSkeleton from './SelectionCardsSkeleton';

type Agent = Omit<AgentInfo, 'timeSlots' | 'agentSpecialties'>;

type AgentsSelectionProps = {
  agents: Agent[];
  selectedAgent: Partial<Agent> | null;
  onSelectAgent: (agent: Agent) => void;
  isLoading?: boolean;
  scrollToSelectedAgent?: boolean;
};

const AgentsSelection: React.FC<AgentsSelectionProps> = ({
  agents,
  selectedAgent,
  onSelectAgent,
  isLoading = false,
  scrollToSelectedAgent = true,
}) => {
  const agentRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  useEffect(() => {
    if (scrollToSelectedAgent && selectedAgent?.agentRecordId) {
      const agentEl = agentRefs.current.get(selectedAgent.agentRecordId);
      agentEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [selectedAgent, scrollToSelectedAgent]);

  const handleSelectAgent = (agent: Agent) => {
    onSelectAgent(agent);
  };

  return (
    <Stack
      direction='column'
      spacing={2}
      data-testid={
        isLoading
          ? 'agentsSelectionContainerLoading'
          : 'agentsSelectionContainer'
      }
      sx={{
        width: '100%',
        my: 2,
        pr: 2,
        overflowY: 'scroll',
        scrollbarColor: 'grey transparent',
      }}
    >
      {isLoading ? (
        <SelectionCardsSkeleton count={4} />
      ) : (
        agents.map((agent) => {
          const isSelected =
            selectedAgent?.agentRecordId === agent.agentRecordId;
          const location = agent.location
            ? `${agent.location} Branch`
            : 'Online Only';
          return (
            <Stack
              key={agent.agentRecordId}
              ref={(el: HTMLDivElement | null) => {
                if (el) agentRefs.current.set(agent.agentRecordId, el);
                else agentRefs.current.delete(agent.agentRecordId);
              }}
              direction='row'
              sx={[
                {
                  backgroundColor: 'background.default',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 2,
                  padding: 2,
                  width: '100%',
                  alignItems: 'center',
                  cursor: 'pointer',
                },
                isSelected && {
                  backgroundColor: '#FCFBF7',
                  borderColor: 'primary.main',
                  borderWidth: '2px',
                },
              ]}
              onClick={() => handleSelectAgent(agent)}
            >
              <ProfileImage src={agent.image} alt={agent.name} size='small' />
              <Stack
                direction='column'
                sx={{
                  ml: 2,
                  flex: 1,
                }}
              >
                <Typography
                  variant='h6Medium'
                  sx={[
                    {
                      textAlign: 'left',
                    },
                    isSelected && {
                      color: 'inherit',
                    },
                  ]}
                >
                  {agent.name}
                </Typography>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Icon iconName='location-dot-duotone' color='primary' />
                  <Typography
                    variant='body1'
                    sx={[
                      {
                        textAlign: 'left',
                      },
                      isSelected && {
                        color: 'inherit',
                      },
                    ]}
                  >
                    {location}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          );
        })
      )}
    </Stack>
  );
};

export default AgentsSelection;
